// InternalRoleRequests.tsx
import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import ReactDataGrid from '@inovua/reactdatagrid-community';
import '@inovua/reactdatagrid-community/index.css';
import { getIdMeBaseApiUrl } from "../../../utils/UrlConstants";
import { useMsal } from '@azure/msal-react';
import { useIdentityAppService } from "../../../services/IdentityServices";

interface InternalRoleRequestsProps {
    refreshTrigger: boolean; // Trigger to reload data
    userId?: any;
}

interface UserAppRequestDto {
    id: number;
    userId: number;
    userLogin: string;
    firstName: string;
    lastName: string;
    userEmail: string;
    applicationCode: string;
    applicationName: string;
    roleCode: string;
    roleName: string;
    assignedTo: string;
    status: string;
    operationalFlag: string;
    createdDate: string;
    modifiedDate: string;
    notes: string;
    environment?: string;
    environmentCode?: string;
    requestedDate?: string;
    approvedBy?: string;
}

const InternalRoleRequests: React.FC<InternalRoleRequestsProps> = ({ refreshTrigger, userId }) => {
    const [userRequests, setUserRequests] = useState<UserAppRequestDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { accounts } = useMsal();

    // Helper function to get current user data from sessionStorage
    const getCurrentUserFromSession = () => {
        try {
            const storedUserData = sessionStorage.getItem('currentUserData');
            return storedUserData ? JSON.parse(storedUserData) : null;
        } catch (error) {
            console.error("Error parsing current user data from session:", error);
            return null;
        }
    };

    useEffect(() => {
        const fetchRoleRequests = async () => {
            // Get user ID from various sources
            let currentUserId = userId;

            // If no userId provided, try to get from session storage
            if (!currentUserId) {
                const sessionUser = getCurrentUserFromSession();
                currentUserId = sessionUser?.userId;
            }

            // If still no user ID, try to get from MSAL account
            if (!currentUserId && accounts.length > 0) {
                currentUserId = accounts[0].localAccountId;
            }

            if (currentUserId) {
                try {
                    setLoading(true);
                    setError(null);

                    console.log("Fetching role requests for userId:", currentUserId);

                    const response = await useIdentityAppService().getUserRequests(currentUserId);

                    // Handle different response formats
                    const requests = response.data.requests || response.data || [];
                    setUserRequests(requests);

                    console.log("Fetched role requests:", requests);
                } catch (error) {
                    console.error("Error fetching role requests:", error);
                    setError("Failed to load role requests");
                    setUserRequests([]);
                } finally {
                    setLoading(false);
                }
            } else {
                console.warn("No user ID found for fetching role requests");
                setError("User information not found");
                setLoading(false);
            }
        };

        fetchRoleRequests();
    }, [refreshTrigger, userId, accounts]); // Include accounts in dependency array

    // Function to get status styles
    const getStatusStyles = (status: string) => {
        switch (status?.toUpperCase()) {
            case 'APPROVED':
            case 'ACTIVE':
                return {
                    backgroundColor: '#E8F5E8',
                    color: '#4CAF50'
                };
            case 'PENDING':
            case 'SUBMITTED':
            case 'REQUESTED':
                return {
                    backgroundColor: '#fff3cd',
                    color: '#856404'
                };
            case 'REJECTED':
            case 'DENIED':
                return {
                    backgroundColor: '#FFEBEE',
                    color: '#f44336'
                };
            case 'IN_REVIEW':
            case 'REVIEWING':
                return {
                    backgroundColor: '#E3F2FD',
                    color: '#1976D2'
                };
            case 'CANCELLED':
            case 'CANCELED':
                return {
                    backgroundColor: '#F5F5F5',
                    color: '#9E9E9E'
                };
            case 'EXPIRED':
                return {
                    backgroundColor: '#F3E5F5',
                    color: '#7B1FA2'
                };
            default:
                return {
                    backgroundColor: '#f5f5f5',
                    color: '#666'
                };
        }
    };

    // Grid columns for role requests
    const requestGridColumns = [
        {
            name: 'id',
            header: 'Request ID',
            defaultWidth: 100,
            type: 'number',
        },
        {
            name: 'applicationName',
            header: 'Application',
            defaultFlex: 1,
            minWidth: 150,
            render: ({ value }: { value: string }) => value || 'N/A'
        },
        {
            name: 'environment',
            header: 'Environment',
            defaultWidth: 120,
            render: ({ value }: { value: string }) => value || 'N/A'
        },
        {
            name: 'roleName',
            header: 'Requested Role',
            defaultFlex: 1,
            minWidth: 150,
            render: ({ value }: { value: string }) => value || 'N/A'
        },
        {
            name: 'status',
            header: 'Status',
            defaultWidth: 120,
            render: ({ value }: { value: string }) => {
                const statusStyles = getStatusStyles(value);

                return (
                    <span style={{
                        padding: '4px 10px',
                        borderRadius: '4px',
                        backgroundColor: statusStyles.backgroundColor,
                        color: statusStyles.color,
                        fontSize: '12px',
                        fontWeight: 'bold',
                        textTransform: 'uppercase'
                    }}>
                        {value || 'Unknown'}
                    </span>
                );
            }
        },
        {
            name: 'assignedTo',
            header: 'Assigned To',
            defaultWidth: 130,
            render: ({ value }: { value: string }) => value || 'N/A'
        },
        {
            name: 'createdDate',
            header: 'Requested Date',
            defaultWidth: 130,
            render: ({ value }: { value: string }) => {
                return value ? new Date(value).toLocaleDateString() : '';
            }
        },
        {
            name: 'modifiedDate',
            header: 'Last Modified',
            defaultWidth: 130,
            render: ({ value }: { value: string }) => {
                return value ? new Date(value).toLocaleDateString() : '';
            }
        }
    ];

    if (loading) {
        return (
            <Box sx={{ mt: 4, padding: 4 }}>
                <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ margin: '0 0 20px 0', color: '#00607f', borderBottom: '2px solid #00607f', paddingBottom: '8px' }}>
                        My Role Requests
                    </h4>
                </div>
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    Loading role requests...
                </div>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ mt: 4, padding: 4 }}>
                <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ margin: '0 0 20px 0', color: '#00607f', borderBottom: '2px solid #00607f', paddingBottom: '8px' }}>
                        My Role Requests
                    </h4>
                </div>
                <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: '#f44336',
                    fontSize: '16px'
                }}>
                    <h5 style={{ marginBottom: '10px' }}>Error Loading Requests</h5>
                    <p>{error}</p>
                </div>
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 4, padding: 4 }}>
            <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 20px 0', color: '#00607f', borderBottom: '2px solid #00607f', paddingBottom: '8px' }}>
                    My Role Requests ({userRequests.length})
                </h4>
            </div>

            {userRequests.length > 0 ? (
                <ReactDataGrid
                    licenseKey="AppName=CoherenceApp,Company=Coherence,ExpiryDate=2025-04-15T00:00:00.000Z,LicenseDeveloperCount=1,LicenseType=single_app,Ref=CoherenceLicenseRef,Z=983320388-1513273079-1127323721-2081795733-199375339958733905"
                    idProperty="id"
                    pagination
                    columns={requestGridColumns}
                    dataSource={userRequests}
                    sortable={true}
                    defaultLimit={10}
                    rowHeight={50}
                    enableColumnAutosize={true}
                    style={{ minHeight: 400 }}
                    defaultSortInfo={[{ name: 'createdDate', dir: -1 }]}
                />
            ) : (
                <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: '#666',
                    fontSize: '16px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px'
                }}>
                    <h5 style={{ marginBottom: '10px' }}>No Role Requests</h5>
                    <p>You have not submitted any role requests yet.</p>
                </div>
            )}
        </Box>
    );
};

export default InternalRoleRequests;